import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import toast from "react-hot-toast";
import hospitalStructureService from "../../../Services/Hospitalstructureservice";
import { IoMdAdd } from "react-icons/io";
import { MdOutlineBedroomParent, MdOutlineHotel } from "react-icons/md";
import { TbEdit } from "react-icons/tb";

const WardRoomPricingList = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        setLoading(true);
        const data = await hospitalStructureService.getAll();
        if (data.responseCode === 200) {
          setRecords(data.structures);
        } else {
          toast.error(data.message || "Failed to fetch pricing records.");
        }
      } catch (error) {
        toast.error("Error fetching pricing records");
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  useEffect(() => {
    const query = searchData.toLowerCase();
    const result =
      records?.filter((item) => {
        const label =
          item.entry_type === "room"
            ? `${item.room_type} ${item.room_number}`
            : `${item.ward_name} ${item.bed_number}`;
        return label.toLowerCase().includes(query);
      }) || [];
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchData, records]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    setPaginatedData(
      filteredData?.slice(startIndex, startIndex + itemsPerPage) || []
    );
  }, [currentPage, filteredData]);

  const SkeletonRow = () => (
    <tr className="border-t border-gray-100 animate-pulse">
      {[...Array(5)].map((_, i) => (
        <td key={i} className="py-3 px-4">
          <div className="h-4 bg-gray-100 rounded-md w-3/4 mx-auto" />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="p-3 sm:p-5 lg:p-6 min-h-screen bg-[#F4F7FB] font-[Outfit,sans-serif]">

      <div className="bg-white border border-gray-100 rounded-2xl px-4 sm:px-6 py-4 mb-5 shadow-sm">

        <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-[16px] sm:text-[18px] font-[600] text-gray-800 tracking-tight">
              Ward &amp; Room Pricing
            </h1>
            <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2F6DB4] inline-block" />
                {filteredData?.length || 0} records found
              </span>
            </p>
          </div>


        <div className="flex items-center gap-4 flex-wrap justify-between w-full">

  <div className="relative w-full sm:w-auto">
    <SearchIcon
      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      style={{ fontSize: 16 }}
    />
    <input
      type="search"
      placeholder="Search by ward name, room type..."
      value={searchData}
      onChange={(e) => setSearchData(e.target.value)}
      className="w-full sm:w-[280px] pl-9 pr-4 py-[8px] text-[13px] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all"
    />
  </div>

  <Link to="add" className="w-full sm:w-auto">
              <button className="relative overflow-hidden bg-[#2F6DB4] text-white text-[12px] sm:text-[13px] font-[500] px-3 sm:px-4 py-2 rounded-lg border border-gray-300 shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 before:absolute before:inset-0 before:-translate-x-full hover:before:translate-x-full before:bg-white/20 before:transition-transform before:duration-500 whitespace-nowrap">
      <IoMdAdd size={16} className="inline mb-0.5" /> 
      Add Pricing
    </button>
  </Link>

</div>
        </div>

    
      </div>

      <div className="hidden md:block bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-[#2F6DB4] to-[#3d82cc] text-[11px] uppercase tracking-wider text-white">
              <th className="py-3 px-4 text-left font-[500] w-12">Sr #</th>
              <th className="py-3 px-4 text-left font-[500]">Bed / Room</th>
              <th className="py-3 px-4 text-center font-[500]">Rate / Day</th>
              <th className="py-3 px-4 text-center font-[500]">Discount</th>
              <th className="py-3 px-4 text-center font-[500]">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
            ) : paginatedData?.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <MdOutlineHotel size={36} className="text-gray-200" />
                    <span className="text-[13px]">No records found</span>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData?.map((row, idx) => (
                <tr
                  key={row.id}
                  className="border-t border-gray-50 hover:bg-blue-50/40 text-[13px] transition-colors duration-150"
                >
                  <td className="py-3.5 px-4 text-gray-400 font-[400]">
                    {(currentPage - 1) * itemsPerPage + idx + 1}
                  </td>

                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                        ${row.entry_type === "room"
                          ? "bg-blue-50 text-blue-500"
                          : "bg-emerald-50 text-emerald-500"}`}>
                        {row.entry_type === "room"
                          ? <MdOutlineBedroomParent size={16} />
                          : <MdOutlineHotel size={16} />}
                      </div>
                      <div>
                        <span className={`inline-block text-[10px] font-[600] px-1.5 py-0.5 rounded-md mr-1.5 uppercase tracking-wide
                          ${row.entry_type === "room"
                            ? "bg-blue-50 text-blue-500"
                            : "bg-emerald-50 text-emerald-600"}`}>
                          {row.entry_type === "room" ? "Room" : "Ward"}
                        </span>
                        <span className="font-[500] text-gray-800">
                          {row.entry_type === "room"
                            ? `${row.room_type} · ${row.room_number}`
                            : `${row.ward_name} · Bed ${row.bed_number}`}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td className="py-3.5 px-4 text-center">
                    <span className="font-[600] text-gray-800">
                      PKR {Number(row.rate_per_day).toLocaleString()}
                    </span>
                    <span className="text-[11px] text-gray-400 ml-1">/ day</span>
                  </td>

                  <td className="py-3.5 px-4 text-center">
                    {row.discount_percent ? (
                      <span className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-600 text-[12px] font-[600] px-2.5 py-0.5 rounded-full border border-amber-100">
                        {row.discount_percent}% off
                      </span>
                    ) : (
                      <span className="text-gray-300 text-[15px]">—</span>
                    )}
                  </td>

                  <td className="py-3.5 px-4 text-center">
                    <button
                      onClick={() => navigate(`update/${row.id}`)}
                      className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[12px] font-[500] px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <TbEdit size={14} />
                      Update
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))
        ) : paginatedData?.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-12 text-center shadow-sm">
            <MdOutlineHotel size={40} className="text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-[13px]">No records found</p>
          </div>
        ) : (
          paginatedData?.map((row, idx) => (
            <div
              key={row.id}
              className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                    ${row.entry_type === "room"
                      ? "bg-blue-50 text-blue-500"
                      : "bg-emerald-50 text-emerald-500"}`}>
                    {row.entry_type === "room"
                      ? <MdOutlineBedroomParent size={18} />
                      : <MdOutlineHotel size={18} />}
                  </div>
                  <div>
                    <span className={`inline-block text-[10px] font-[600] px-1.5 py-0.5 rounded-md mr-1 uppercase tracking-wide
                      ${row.entry_type === "room"
                        ? "bg-blue-50 text-blue-500"
                        : "bg-emerald-50 text-emerald-600"}`}>
                      {row.entry_type === "room" ? "Room" : "Ward"}
                    </span>
                    <p className="text-[13px] font-[600] text-gray-800 mt-0.5">
                      {row.entry_type === "room"
                        ? `${row.room_type} · ${row.room_number}`
                        : `${row.ward_name} · Bed ${row.bed_number}`}
                    </p>
                  </div>
                </div>

                <span className="text-[11px] text-gray-300 font-[500] flex-shrink-0">
                  #{(currentPage - 1) * itemsPerPage + idx + 1}
                </span>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-[500]">Rate / Day</p>
                    <p className="text-[14px] font-[700] text-gray-800">
                      PKR {Number(row.rate_per_day).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide font-[500]">Discount</p>
                    {row.discount_percent ? (
                      <span className="inline-flex items-center bg-amber-50 text-amber-600 text-[11px] font-[600] px-2 py-0.5 rounded-full border border-amber-100">
                        {row.discount_percent}% off
                      </span>
                    ) : (
                      <span className="text-gray-300 text-[15px] leading-none">—</span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => navigate(`update/${row.id}`)}
                  className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-[12px] font-[500] px-3.5 py-1.5 rounded-xl transition-colors"
                >
                  <TbEdit size={14} />
                  Update
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-4">
        <PaginationComponent
          filteredData={filteredData}
          setPaginatedData={setPaginatedData}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default WardRoomPricingList;